<template>
  <div
    id="login"
    class="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8"
  >
    <div class="sm:mx-auto sm:w-full sm:max-w-md">
      <AppLogo class="logo h-10 w-auto mx-auto mb-4" />
    </div>

    <div class="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
      <div class="bg-white px-6 py-12 shadow sm:rounded-lg sm:px-12">
        <form class="space-y-6">
          <!--  class="w-full p-4 max-w-md m-auto flex flex-col gap-4" -->

          <div v-if="localAuthMethods.includes('google')" class="mb-6">
            <button
              class="signin-google bg-[#4285f4] hover:bg-[#2a75f3] flex justify-between items-stretch  w-full rounded-lg text-white"
              @click.prevent="loginOrSignUpGoogleUser"
            >
              <!-- eslint-disable -->
              <span class="px-5 py-2.5 border-r border-white/50">
                <svg class="size-5 fill-current self-center"  xmlns="http://www.w3.org/2000/svg" aria-labelledby="a56pck3i04glya0p5qgj76yizpdi55l" role="img" viewBox="0 0 24 24">
                  <title id="a56pck3i04glya0p5qgj76yizpdi55l">Google icon</title>
                  <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z"></path>
                </svg>
              </span>
              <!-- eslint-enable -->
              <span class="px-2 py-2.5 ml-4 text-sm font-semibold">
                {{ authType == 'login' ? 'Sign in with Google' : 'Sign up with Google' }}
              </span>
              <span class="px-5 py-2.5 ">
                <span class="size-4 i-heroicons-arrow-right" />
              </span>
            </button>
          </div>

          <div class="relative my-10">
            <div class="absolute inset-0 flex items-center" aria-hidden="true">
              <div class="w-full border-t border-slate-300" />
            </div>
            <div class="relative flex justify-center text-sm font-medium leading-6">
              <span class="bg-white px-6 text-white">Or continue with</span>
            </div>
          </div>

          <div 
            v-if="localAuthMethods.includes('email') || localAuthMethods.includes('emailCustom')"
            class="flex flex-col gap-4"
          >
            <label
              v-if="authType == 'signup'"
              class="sr-only"
              for="name-input"
            >Name</label>
            <input
              v-if="authType == 'signup'"
              id="name-input"
              v-model="name"
              placeholder="Name"
            />

            <label
              class="sr-only"
              for="email-input"
            >Email address</label>
            <input
              id="email-input"
              v-model="email"
              placeholder="Email address"
            />

            <label
              class="sr-only"
              for="password-input"
            >Password</label>
            <div class="relative">
              <input
                id="password-input"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                class="mb-4t w-full"
                placeholder="Password"
              />
              <button
                type="button"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                @click="showPassword = !showPassword"
              >
                <span :class="showPassword ? 'i-heroicons-eye' : 'i-heroicons-eye-slash'" class="size-5" />
              </button>
            </div>

            <!-- Login email -->
            <BaseButton
              v-if="localAuthMethods.includes('email') && authType == 'login'"
              key="login-button"
              type="submit"
              variant="secondary"
              @click.prevent="loginEmailuser"
            >
              Log in
            </BaseButton>
            <!-- Register email user custom -->
            <BaseButton
              v-if="localAuthMethods.includes('email') && authType == 'signup'"
              key="sinup-button"
              type="submit"
              variant="secondary"
              @click.prevent="registernEmailUser"
            >
              Sign up
            </BaseButton>
          </div>
        </form>
      </div>

      <p class="copyright mt-5 mb-3 text-white text-center" >
        <span v-if="authType == 'login' && showSignup">
          Dont have an account?
          <RouterLink to="/signup" class="font-semibold text-white">Sign up</RouterLink>
        </span>
        <span v-else-if="authType == 'signup'">
          Already have an account?
          <RouterLink to="/login" class="font-semibold text-white"> Log in</RouterLink>
        </span>
      </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onBeforeMount } from 'vue'
import AppLogo from '@/assets/images/logo-white.svg'
import BaseButton from '@/components/base/BaseButton.vue'

const props = defineProps({
  authDomain: {
    type: String,
    required: false,
    default: ''
  },
  showSignup: {
    type: Boolean,
    default: true
  },
  authType: {
    type: String,
    required: true,
    default: 'login'
  },
  authMethods: {
    type: Array,
    required: true,
    default: () => (['email', 'google'])
  }
})

const emit = defineEmits(['onLoginOrSignUpGoogleUser', 'onLoginEmailuser', 'onRegisternEmailUser'])

const name = ref('')
const email = ref('')
const password = ref('')
const showPassword = ref(false)
const localAuthMethods = props.authMethods.slice()

const loginOrSignUpGoogleUser = () => {
  emit('onLoginOrSignUpGoogleUser')
}

const loginEmailuser = () => {
  emit('onLoginEmailuser', {
    email: email.value,
    password: password.value,
  })
}

const registernEmailUser = () => {
  emit('onRegisternEmailUser', {
    name: name.value,
    email: email.value,
    password: password.value,
    domain: props.authDomain
  })
}

onBeforeMount(() => {
  // cleanup authMethods array
  if (localAuthMethods.includes('emailCustom')) {
    const index = localAuthMethods.indexOf('email')
    if (index !== -1) {
      localAuthMethods.splice(index, 1)
    }
  }
})
</script>
